'use client';

import React, { useEffect, useRef } from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

const stats = [
{ value: '25+', label: 'Years of Excellence' },
{ value: '5000+', label: 'Alumni Placed' },
{ value: '80+', label: 'Expert Faculty' },
{ value: '95%', label: 'Placement Rate' }];


export default function HeroSection() {
  const imgRef = useRef<HTMLDivElement>(null);
  const headlineRef = useRef<HTMLHeadingElement>(null);
  const statsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Parallax on scroll
    const handleScroll = () => {
      if (!imgRef?.current) return;
      const y = window.scrollY * 0.35;
      imgRef.current.style.transform = `translateY(${y}px)`;
    };
    window.addEventListener('scroll', handleScroll, { passive: true });

    // Headline word-by-word reveal
    if (headlineRef?.current) {
      const words = headlineRef?.current?.querySelectorAll<HTMLElement>('.word-inner');
      words?.forEach((word, i) => {
        word.style.transition = `transform 0.9s cubic-bezier(0.16,1,0.3,1) ${i * 0.08}s, opacity 0.9s ease ${i * 0.08}s`;
        word.style.transform = 'translateY(100%)';
        word.style.opacity = '0';
        word.style.display = 'inline-block';
      });
      const timer = setTimeout(() => {
        words?.forEach((word) => {
          word.style.transform = 'translateY(0)';
          word.style.opacity = '1';
        });
      }, 1400);
      return () => {
        clearTimeout(timer);
        window.removeEventListener('scroll', handleScroll);
      };
    }

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Animate stats counter
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const counters = entry.target.querySelectorAll<HTMLElement>('[data-count]');
            counters.forEach((el) => {
              el.style.opacity = '1';
              el.style.transform = 'translateY(0)';
            });
            observer.disconnect();
          }
        });
      },
      { threshold: 0.3 }
    );
    if (statsRef?.current) observer?.observe(statsRef?.current);
    return () => observer?.disconnect();
  }, []);

  const headlineWords = ['Shaping', 'Future', 'Pharmacists', 'Through', 'Excellence'];

  return (
    <section data-component-id="src/app/components/HeroSection.tsx:77:4" data-component-path="src/app/components/HeroSection.tsx" data-component-line="77" data-component-end-line="153" data-component-file="HeroSection.tsx" data-component-name="section" data-component-content="%7B%22elementName%22%3A%22section%22%2C%22className%22%3A%22relative%20min-h-screen%20flex%20flex-col%20items-center%20justify-start%20overflow-hidden%22%7D" className="relative min-h-screen flex flex-col items-center justify-start overflow-hidden">
      {/* Background image with parallax */}
      <div data-component-id="src/app/components/HeroSection.tsx:79:6" data-component-path="src/app/components/HeroSection.tsx" data-component-line="79" data-component-end-line="92" data-component-file="HeroSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20inset-0%20w-full%20h-full%22%7D" className="absolute inset-0 w-full h-full">
        <div data-component-id="src/app/components/HeroSection.tsx:80:8" data-component-path="src/app/components/HeroSection.tsx" data-component-line="80" data-component-end-line="89" data-component-file="HeroSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20inset-0%20w-full%20h-%5B115%25%5D%20-top-%5B7.5%25%5D%20parallax-slow%22%7D" ref={imgRef} className="absolute inset-0 w-full h-[115%] -top-[7.5%] parallax-slow">
          <AppImage data-component-id="src/app/components/HeroSection.tsx:81:10" data-component-path="src/app/components/HeroSection.tsx" data-component-line="81" data-component-end-line="87" data-component-file="HeroSection.tsx" data-component-name="AppImage" data-component-content="%7B%22elementName%22%3A%22AppImage%22%2C%22src%22%3A%22%2Fassets%2Fimages%2Fhome-banner.png%22%2C%22alt%22%3A%22Pharmacy%20laboratory%20with%20modern%20equipment%2C%20bright%20professional%20environment%2C%20scientific%20research%20setting%22%2C%22className%22%3A%22object-cover%22%7D"
            src="/assets/images/home-banner.png"
            alt="Pharmacy laboratory with modern equipment, bright professional environment, scientific research setting"
            fill
            priority
            className="object-cover"
            sizes="100vw" />
          
        </div>
        {/* Scrim overlay — dark enough for white text */}
        <div data-component-id="src/app/components/HeroSection.tsx:91:8" data-component-path="src/app/components/HeroSection.tsx" data-component-line="91" data-component-end-line="91" data-component-file="HeroSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20inset-0%20bg-black%2F0%22%7D" className="absolute inset-0 bg-black/0" />
      </div>
      {/* Content */}
      <div data-component-id="src/app/components/HeroSection.tsx:94:6" data-component-path="src/app/components/HeroSection.tsx" data-component-line="94" data-component-end-line="130" data-component-file="HeroSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22relative%20z-10%20max-w-7xl%20mx-auto%20px-6%20w-full%20pt-32%20pb-20%22%7D" className="relative z-10 max-w-7xl mx-auto px-6 w-full pt-32 pb-20">
        <div data-component-id="src/app/components/HeroSection.tsx:95:8" data-component-path="src/app/components/HeroSection.tsx" data-component-line="95" data-component-end-line="126" data-component-file="HeroSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22max-w-4xl%20mx-auto%20text-center%20flex%20flex-col%20items-center%22%7D" className="max-w-4xl mx-auto text-center flex flex-col items-center">
          {/* Label */}
          <div data-component-id="src/app/components/HeroSection.tsx:97:10" data-component-path="src/app/components/HeroSection.tsx" data-component-line="97" data-component-end-line="105" data-component-file="HeroSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22inline-flex%20items-center%20gap-2%20px-4%20py-1.5%20rounded-full%20border%20border-white%2F25%20bg-white%2F10%20backdrop-blur-sm%20mb-8%22%7D"
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-white/25 bg-white/10 backdrop-blur-sm mb-8"
            style={{ opacity: 0, animation: 'fadeIn 0.8s ease 1.2s forwards' }}>
            
            
            <span data-component-id="src/app/components/HeroSection.tsx:102:12" data-component-path="src/app/components/HeroSection.tsx" data-component-line="102" data-component-end-line="104" data-component-file="HeroSection.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-xs%20font-semibold%20text-white%2F90%20tracking-widest%20uppercase%22%2C%22textContent%22%3A%22PCI%20%26%20BTE%20Accredited%20Institution%22%7D" className="text-xs font-semibold text-white/90 tracking-widest uppercase">
              PCI & BTE Accredited Institution
            </span>
          </div>

          {/* Headline */}
          <h1 data-component-id="src/app/components/HeroSection.tsx:108:10" data-component-path="src/app/components/HeroSection.tsx" data-component-line="108" data-component-end-line="121" data-component-file="HeroSection.tsx" data-component-name="h1" data-component-content="%7B%22elementName%22%3A%22h1%22%2C%22className%22%3A%22text-hero%20text-%5B%230a3386%5D%20mb-6%20leading-none%20text-center%22%7D"
  ref={headlineRef}
  className="text-hero text-[#0a3386] mb-6 leading-none text-center"
  aria-label="Shaping Future Pharmacists Through Excellence"
>
  {headlineWords?.map((word, i) => (
    <span data-component-id="src/app/components/HeroSection.tsx:114:4" data-component-path="src/app/components/HeroSection.tsx" data-component-line="114" data-component-end-line="119" data-component-file="HeroSection.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22reveal-word%20mr-3%20md%3Amr-4%20inline-block%20overflow-hidden%20align-top%22%7D"
      key={i}
      className="reveal-word mr-3 md:mr-4 inline-block overflow-hidden align-top"
    >
      <span data-component-id="src/app/components/HeroSection.tsx:118:6" data-component-path="src/app/components/HeroSection.tsx" data-component-line="118" data-component-end-line="118" data-component-file="HeroSection.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22word-inner%22%7D" className="word-inner">{word}</span>
    </span>
  ))}
</h1>

          

          
        </div>

        {/* Floating Stats Card */}
        
      </div>
      {/* Scroll indicator */}
      <div data-component-id="src/app/components/HeroSection.tsx:132:6" data-component-path="src/app/components/HeroSection.tsx" data-component-line="132" data-component-end-line="146" data-component-file="HeroSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20bottom-8%20left-1%2F2%20-translate-x-1%2F2%20flex%20flex-col%20items-center%20gap-2%22%7D"
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2"
        style={{ opacity: 0, animation: 'fadeIn 0.8s ease 2.4s forwards' }}>
        
        <span data-component-id="src/app/components/HeroSection.tsx:136:8" data-component-path="src/app/components/HeroSection.tsx" data-component-line="136" data-component-end-line="136" data-component-file="HeroSection.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-white%2F40%20text-xs%20tracking-widest%20uppercase%22%2C%22textContent%22%3A%22Scroll%22%7D" className="text-white/40 text-xs tracking-widest uppercase">Scroll</span>
        <div data-component-id="src/app/components/HeroSection.tsx:137:8" data-component-path="src/app/components/HeroSection.tsx" data-component-line="137" data-component-end-line="145" data-component-file="HeroSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22w-px%20h-10%20bg-white%2F20%20overflow-hidden%22%7D" className="w-px h-10 bg-white/20 overflow-hidden">
          <div data-component-id="src/app/components/HeroSection.tsx:138:10" data-component-path="src/app/components/HeroSection.tsx" data-component-line="138" data-component-end-line="143" data-component-file="HeroSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22w-full%20bg-white%2F60%22%7D"
            className="w-full bg-white/60"
            style={{
              height: '40%',
              animation: 'scrollLine 1.8s ease-in-out infinite'
            }} />
          
        </div>
      </div>
      <style data-component-id="src/app/components/HeroSection.tsx:147:6" data-component-path="src/app/components/HeroSection.tsx" data-component-line="147" data-component-end-line="152" data-component-file="HeroSection.tsx" data-component-name="style" data-component-content="%7B%22elementName%22%3A%22style%22%7D" jsx>{`
        @keyframes scrollLine {
          0% { transform: translateY(-100%); }
          100% { transform: translateY(300%); }
        }
      `}</style>
    </section>);

}